import React, { useState, useEffect } from 'react';
import {
  LayoutDashboard, Users, Building2, ShieldAlert,
  BookOpen, CreditCard, LogOut, Zap, ChevronRight, Shield
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';


const navItems = [
  { id: 'metrics', label: 'System Metrics', icon: LayoutDashboard },
  { id: 'users', label: 'User Management', icon: Users },
  { id: 'partners', label: 'Partner Management', icon: Building2, hasBadge: true },
  { id: 'fraud', label: 'Fraud Monitoring', icon: ShieldAlert },
  { id: 'content', label: 'Content Management', icon: BookOpen },
  { id: 'subscriptions', label: 'Subscription Mgmt', icon: CreditCard },
];

export default function AdminSidebar({ activeSection, onSectionChange, onSignOut }) {
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    const loadPendingCount = async () => {
      try {
        const { count } = await supabase
          ?.from('partner_applications')
          ?.select('*', { count: 'exact', head: true })
          ?.eq('application_status', 'pending');
        setPendingCount(count || 0);
      } catch (err) {
        console.error('Pending count error:', err);
      }
    };

    loadPendingCount();

    const channel = supabase?.channel('sidebar_applications_badge')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'partner_applications' }, () => {
        loadPendingCount();
      })
      ?.subscribe();

    return () => supabase?.removeChannel(channel);
  }, []);

  return (
    <aside className="w-64 min-h-screen flex flex-col" style={{ background: 'rgba(13,27,42,0.97)', borderRight: '1px solid rgba(0,229,255,0.1)' }}>
      {/* Logo */}
      <div className="px-6 py-5 border-b" style={{ borderColor: 'rgba(0,229,255,0.1)' }}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #00E5FF22, #7B2FBE22)', border: '1px solid rgba(0,229,255,0.3)' }}>
            <Zap size={18} style={{ color: '#00E5FF' }} />
          </div>
          <div>
            <p className="text-white font-semibold text-sm" style={{ fontFamily: 'DM Sans, sans-serif' }}>FinVisAR</p>
            <p className="text-xs" style={{ color: '#7B2FBE', fontFamily: 'DM Sans, sans-serif' }}>Admin Console</p>
          </div>
        </div>
      </div>

      {/* Admin Badge */}
      <div className="px-4 py-4 border-b" style={{ borderColor: 'rgba(0,229,255,0.08)' }}>
        <div className="rounded-xl p-3" style={{ background: 'rgba(123,47,190,0.08)', border: '1px solid rgba(123,47,190,0.2)' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #7B2FBE, #00E5FF)' }}>
              <Shield size={18} className="text-white" />
            </div>
            <div>
              <p className="text-white text-sm font-medium" style={{ fontFamily: 'DM Sans, sans-serif' }}>Admin Access</p>
              <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ color: '#7B2FBE', background: 'rgba(123,47,190,0.15)', fontFamily: 'DM Sans, sans-serif' }}>Super Admin</span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1">
        {navItems?.map((item) => {
          const Icon = item?.icon;
          const isActive = activeSection === item?.id;
          const showBadge = item?.hasBadge && pendingCount > 0;
          return (
            <button
              key={item?.id}
              onClick={() => onSectionChange(item?.id)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-200 group"
              style={{
                background: isActive ? 'linear-gradient(135deg, rgba(0,229,255,0.12), rgba(123,47,190,0.12))' : 'transparent',
                border: isActive ? '1px solid rgba(0,229,255,0.2)' : '1px solid transparent',
              }}
            >
              <Icon size={17} style={{ color: isActive ? '#00E5FF' : '#64748B', transition: 'color 0.2s' }} />
              <span className="text-sm font-medium flex-1" style={{ color: isActive ? '#E2E8F0' : '#64748B', fontFamily: 'DM Sans, sans-serif', transition: 'color 0.2s' }}>
                {item?.label}
              </span>
              {showBadge && (
                <span className="text-xs font-bold px-1.5 py-0.5 rounded-full" style={{ background: '#EF4444', color: '#fff', fontFamily: 'DM Sans, sans-serif', minWidth: '18px', textAlign: 'center' }}>
                  {pendingCount}
                </span>
              )}
              {isActive && !showBadge && <ChevronRight size={14} style={{ color: '#00E5FF' }} />}
            </button>
          );
        })}
      </nav>

      {/* Sign Out */}
      <div className="px-3 pb-6">
        <button onClick={onSignOut} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200" style={{ border: '1px solid rgba(239,68,68,0.15)' }}>
          <LogOut size={17} style={{ color: '#EF4444' }} />
          <span className="text-sm font-medium" style={{ color: '#EF4444', fontFamily: 'DM Sans, sans-serif' }}>Sign Out</span>
        </button>
      </div>
    </aside>
  );
}
